import { loadDevMessages, loadErrorMessages } from "@apollo/client/dev";
import { Suspense, use, useState } from "react";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import { createBrowserRouter, RouterProvider } from "react-router";

import { Loading } from "design-system/components/Loading";
import { FailedToLoad } from "shell/console/components/FailedToLoad";
import { setRouteConfig } from "shell/platform/lib/analytics/amplitudeRoutes";
import { logError } from "shell/platform/lib/analytics/rum";
import { getRouterConfig } from "shell/routes/routes";

if (import.meta.env.DEV) {
  loadDevMessages();
  loadErrorMessages();
}

const loadRouter = async (
  configPromise: ReturnType<typeof getRouterConfig>,
) => {
  const config = await configPromise;
  setRouteConfig(config);
  return createBrowserRouter(config);
};

const RouterLoader = ({
  routerPromise,
}: {
  routerPromise: ReturnType<typeof loadRouter>;
}) => {
  const router = use(routerPromise);

  return <RouterProvider router={router} />;
};

const RouterLoadError = (_props: FallbackProps) => (
  <FailedToLoad resource="app" onRetry={() => window.location.reload()} />
);

export const App = () => {
  const [routerPromise] = useState(() => loadRouter(getRouterConfig()));

  return (
    <Suspense fallback={<Loading isFullScreen size="large" />}>
      <ErrorBoundary FallbackComponent={RouterLoadError} onError={logError}>
        <RouterLoader routerPromise={routerPromise} />
      </ErrorBoundary>
    </Suspense>
  );
};
